// src/pages/Home.js  
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { Typography, CircularProgress, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProductCard from '../components/Product/ProductCard/ProductCard';

const Home = ({ department: propDepartment }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const searchTerm = searchParams.get('search') || '';
  
  // Determine department from URL path or prop
  const getDepartmentFromPath = () => {
    const path = location.pathname;
    if (path.includes('/department/men')) return 'الرجال';
    if (path.includes('/department/women')) return 'النساء';
    if (path.includes('/department/children')) return 'الأطفال';
    return propDepartment || 'الأطفال';
  };
  
  const department = getDepartmentFromPath();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchSubcategories = async (departmentName) => {
      try {
        // Find the main category
        const { data: departmentData, error: mainError } = await supabase
          .from('categories')
          .select('*')
          .eq('name', departmentName)
          .is('parent_id', null)
          .single();

        if (mainError) {
          console.error('Main category not found:', departmentName);
          return [];
        }

        // Get subcategories
        const { data: subcategoriesData, error: subError } = await supabase
          .from('categories')
          .select('*')
          .eq('parent_id', departmentData.id);

        if (subError) throw subError;

        return subcategoriesData || [];
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        return [];
      }
    };

    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Always fetch subcategories for the main category
        const subs = await fetchSubcategories(department);
        setSubcategories(subs);
        
        let productsQuery = supabase
          .from('products')
          .select('*, categories(name)');
          
        // If there's a search term, filter by search AND department subcategories
        if (searchTerm && subs.length > 0) {
          const subcategoryIds = subs.map(sub => sub.id);
          productsQuery = productsQuery
            .ilike('name_ar', `%${searchTerm}%`)
            .in('category_id', subcategoryIds);
        } else if (searchTerm) {
          // If no subcategories found but there's a search term, search within current department only
          // This will return no results if department has no subcategories
          productsQuery = productsQuery
            .ilike('name_ar', `%${searchTerm}%`)
            .in('category_id', []); // Empty array will return no results
        }
        
        const { data: productsData, error: productsError } = await productsQuery;
        
        if (productsError) throw productsError;
        
        const transformedProducts = await Promise.all(productsData.map(async (product) => {
          try {
            // Updated query to include new fields and order by color_order
            const { data: colorData, error: colorError } = await supabase
              .from('product_colors')
              .select(`
                *,
                colors(name),
                product_color_images(image_url, image_order)
              `)
              .eq('product_id', product.id)
              .order('color_order');
              
            if (colorError) throw colorError;
            
            // Find the home color first, fallback to first color if no home color is set
            let homeColor = colorData.find(color => color.is_home_color) || colorData[0] || null;
            let stock = 0;
            let images = ['/placeholder-image.png'];
            
            if (homeColor) {
              // Get sizes for the home color - only need stock info now
              const { data: sizeData, error: sizeError } = await supabase
                .from('product_color_sizes')
                .select('stock')
                .eq('product_color_id', homeColor.id)
                .order('size');
                
              if (sizeError) throw sizeError;
              
              if (sizeData && sizeData.length > 0) {
                // Sum up stock across all sizes for this color
                stock = sizeData.reduce((total, size) => total + (size.stock || 0), 0);
              }
              
              // Get images for the home color, ordered by image_order
              const { data: imageData, error: imageError } = await supabase
                .from('product_color_images')
                .select('*')
                .eq('product_color_id', homeColor.id)
                .order('image_order');
                
              if (imageError) throw imageError;
              
              if (imageData && imageData.length > 0) {
                images = imageData.map(img => img.image_url);
              }
            }
            
            const categoryName = 
              (product.categories && product.categories.name) || 
              'غير مصنف';
            
            return {
              id: product.id,
              name_ar: product.name_ar,
              description_ar: product.description_ar,
              price: product.price || 0, // Get price from products table
              newPrice: product.new_price || null, // Get new_price from products table
              stock: stock,
              images: images,
              product_colors: colorData || [],
              home_color: homeColor,
              category: categoryName,
              category_id: product.category_id
            };
          } catch (error) {
            console.error(`Error processing product ${product.id}:`, error);
            return {
              id: product.id,
              name_ar: product.name_ar,
              description_ar: product.description_ar,
              price: product.price || 0, // Get price from products table
              newPrice: product.new_price || null, // Get new_price from products table
              stock: 0,
              images: ['/placeholder-image.png'],
              product_colors: [],
              home_color: null,
              category: 'غير مصنف',
              category_id: product.category_id
            };
          }
        }));
        
        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchTerm, department]);

  // Since we're now filtering at the database level, we can simplify the client-side filtering
  const filteredProducts = products.filter(product => {
    // If there's a search term, products are already filtered by department in the query
    if (searchTerm) {
      return true;
    }
    
    // If no search term, filter by subcategories of the current department
    if (subcategories.length > 0) {
      return subcategories.some(subcategory => subcategory.name === product.category);
    }
    // If no subcategories found for this main category, don't show any products
    return false;
  });

  // Group filtered products by categories
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'غير مصنف';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  return (
    <Box sx={{ bgcolor: '#f9f9f9', minHeight: '100vh' }}>
      {loading ? (
        <Box display="flex" justifyContent="center" pt={8}>
          <CircularProgress />
        </Box>
      ) : filteredProducts.length === 0 ? (
        <Box sx={{ textAlign: 'center', pt: 8 }}>
          <Typography variant="h6">
            {searchTerm 
              ? `لا توجد منتجات تطابق البحث "${searchTerm}" في فئة ${department}`
              : `لا توجد منتجات متاحة في فئة ${department}`
            }
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 1, md: 3 } }}>
          {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
            <Box key={category} sx={{ mb: { xs: 3, md: 6 } }}>
              {/* Split Category Bar - FIXED HEIGHT STRUCTURE */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'stretch', 
                mb: { xs: 1.5, md: 3 },
                borderRadius: { xs: 1.5, md: 2 },
                overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(15, 54, 98, 0.3)',
                // FIXED HEIGHT TO PREVENT JUMPING
                height: { xs: '38px', md: '46px' },
              }}>
                {/* Category Section (wider) */}
                <Box sx={{
                  flex: '2.9',
                  px: { xs: 1, md: 2 },
                  background: '#0f3662',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex', // Added flex display
                  alignItems: 'center', // Center content vertically
                  // Cool animated background pattern
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                      radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)
                    `,
                    pointerEvents: 'none',
                  },
                  // Subtle animated shimmer effect
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)',
                    animation: 'shimmer 3s infinite',
                    pointerEvents: 'none',
                  },
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%) translateY(-100%)' },
                    '100%': { transform: 'translateX(100%) translateY(100%)' }
                  }
                }}>
                  <Typography 
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: '900',
                      color: '#ffffff',
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                      textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {category}
                  </Typography>
                </Box>

                {/* "مزيد" Button Section - STABLE HEIGHT WITH GRADIENT */}
                <Box 
                  component={Link}
                  to={`${location.pathname}/${encodeURIComponent(category)}`}
                  sx={{
                    flex: '0.5',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #0f3662',
                    borderTopLeftRadius: { xs: '8px', md: '12px' },
                    borderBottomLeftRadius: { xs: '8px', md: '12px' },
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      background: '#0f3662',
                      boxShadow: '0 3px 8px rgba(15, 54, 98, 0.3)',
                      '& .more-button-text': {
                        color: '#ffffff',
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                      },
                      // Animated background pattern on hover - POSITIONED ABSOLUTELY TO NOT AFFECT HEIGHT
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `
                          radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                          radial-gradient(circle at 40% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)
                        `,
                        pointerEvents: 'none',
                        zIndex: 0,
                      },
                      // Subtle animated shimmer effect on hover - POSITIONED ABSOLUTELY
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)',
                        animation: 'shimmer 3s infinite',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }
                    },
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(-100%) translateY(-100%)' },
                      '100%': { transform: 'translateX(100%) translateY(100%)' }
                    }
                  }}
                >
                  <Typography 
                    className="more-button-text"
                    sx={{ 
                      color: '#0f3662',
                      fontWeight: '900',
                      fontSize: { xs: '0.7rem', md: '0.95rem' },
                      transition: 'color 0.3s ease, text-shadow 0.3s ease',
                      // ENSURE TEXT DOESN'T AFFECT HEIGHT AND STAYS ON TOP
                      lineHeight: 1,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    المزيد
                  </Typography>
                </Box>
              </Box>

              {/* Mobile Layout - Horizontal Scroll with optimized spacing */}
              {isMobile ? (
                <Box sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 1,
                  px: 1,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}>
                  {categoryProducts.slice(0, 4).map((product) => (
                    <Box
                      key={product.id}
                      sx={{
                        minWidth: '140px', // Fixed width for consistency
                        maxWidth: '140px',
                        flexShrink: 0,
                      }}
                    >
                      <ProductCard product={product} />
                    </Box>
                  ))}
                </Box>
              ) : (
                /* Desktop Layout - Grid with improved responsive breakpoints */
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(5, 1fr)' // Added xl breakpoint for larger screens
                  },
                  gap: { sm: 2, md: 3 }
                }}>
                  {categoryProducts.slice(0, isMobile ? 4 : 8).map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Home;
